import { useCallback, useEffect, useRef, useState } from "react";

import {
  ACCOUNT_SYNC_EVENT,
  appStateHasProgress,
  collectLocalAppState,
  restoreLocalAppState,
} from "../lib/accountStorage";
import { supabase } from "../lib/supabaseClient";

const AUTOSAVE_DELAY_MS = 1800;
const ACTIVE_SUBSCRIPTION_STATES = new Set(["lifetime", "active", "trialing"]);

function getStateTime(state) {
  const raw = state?.changedAt || state?.exportedAt;
  if (!raw) return 0;
  const time = new Date(raw).getTime();
  return Number.isNaN(time) ? 0 : time;
}

function getRemoteTime(row) {
  const stateTime = getStateTime(row?.state);
  const updatedTime = row?.updated_at ? new Date(row.updated_at).getTime() : 0;
  return Math.max(stateTime, Number.isNaN(updatedTime) ? 0 : updatedTime);
}

export function useAccountAutosave() {
  const [status, setStatus] = useState({
    signedIn: false,
    saving: false,
    savedAt: "",
    error: "",
    isPro: false,
  });
  const sessionRef = useRef(null);
  const timerRef = useRef(null);
  const savingRef = useRef(false);
  const lastSavedChangedAtRef = useRef("");

  const autosave = useCallback(async ({ force = false } = {}) => {
    const session = sessionRef.current;
    if (!supabase || !session?.user || savingRef.current) return;

    const appState = collectLocalAppState();
    if (!force && !Object.keys(appState.values || {}).length) return;
    // Guard: a browser with no real progress (fresh device, cleared storage)
    // must never overwrite an existing cloud backup automatically. Its local
    // keys like "last open tab" carry a fresh changedAt that would otherwise
    // win the timestamp check below. Manual "Back up now" (force) still can.
    if (!force && !appStateHasProgress(appState)) return;
    if (!force && appState.changedAt && appState.changedAt === lastSavedChangedAtRef.current) return;

    savingRef.current = true;
    setStatus((current) => ({ ...current, signedIn: true, saving: true, error: "" }));

    const { data: existingBackup, error: readError } = await supabase
      .from("user_app_state")
      .select("state, updated_at")
      .eq("user_id", session.user.id)
      .maybeSingle();

    if (readError) {
      savingRef.current = false;
      setStatus((current) => ({ ...current, saving: false, error: readError.message }));
      return;
    }

    if (!force && existingBackup && getRemoteTime(existingBackup) > getStateTime(appState)) {
      savingRef.current = false;
      setStatus((current) => ({ ...current, saving: false }));
      return;
    }

    const savedAt = new Date().toISOString();
    const { error: writeError } = await supabase
      .from("user_app_state")
      .upsert({
        user_id: session.user.id,
        state: appState,
        updated_at: savedAt,
      });

    savingRef.current = false;

    if (writeError) {
      setStatus((current) => ({ ...current, saving: false, error: writeError.message }));
      return;
    }

    lastSavedChangedAtRef.current = appState.changedAt || appState.exportedAt;
    setStatus((current) => ({ ...current, signedIn: true, saving: false, savedAt, error: "" }));
  }, []);

  const scheduleAutosave = useCallback((options = {}) => {
    window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      autosave(options.force ? options : {});
    }, options.force || options.immediate ? 0 : AUTOSAVE_DELAY_MS);
  }, [autosave]);

  useEffect(() => {
    if (!supabase) return undefined;

    let mounted = true;

    async function restoreNewerCloudState(session) {
      if (!session?.user) return;
      const { data: remote, error } = await supabase
        .from("user_app_state")
        .select("state, updated_at")
        .eq("user_id", session.user.id)
        .maybeSingle();
      if (error || !remote?.state) return;
      const local = collectLocalAppState();
      if (!appStateHasProgress(local) || getRemoteTime(remote) > getStateTime(local)) {
        restoreLocalAppState(remote.state);
        lastSavedChangedAtRef.current = remote.state.changedAt || remote.state.exportedAt || "";
      }
    }

    async function hydrateSession() {
      const { data } = await supabase.auth.getSession();
      if (!mounted) return;

      sessionRef.current = data.session;
      setStatus((current) => ({
        ...current,
        signedIn: Boolean(data.session?.user),
      }));

      if (data.session?.user) {
        await restoreNewerCloudState(data.session);
        if (!mounted) return;
        scheduleAutosave();

        const { data: subscription } = await supabase
          .from("user_subscriptions")
          .select("subscription_status")
          .eq("user_id", data.session.user.id)
          .maybeSingle();

        if (mounted) {
          setStatus((current) => ({
            ...current,
            isPro: ACTIVE_SUBSCRIPTION_STATES.has(subscription?.subscription_status),
          }));
        }
      }
    }

    hydrateSession();

    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      sessionRef.current = session;
      setStatus((current) => ({
        ...current,
        signedIn: Boolean(session?.user),
      }));
      if (session?.user) {
        window.setTimeout(() => {
          restoreNewerCloudState(session).finally(() => {
            if (mounted) scheduleAutosave();
          });
        }, 0);
      }
    });

    return () => {
      mounted = false;
      window.clearTimeout(timerRef.current);
      data.subscription.unsubscribe();
    };
  }, [scheduleAutosave]);

  useEffect(() => {
    function handleProgressChanged() {
      scheduleAutosave();
    }

    window.addEventListener(ACCOUNT_SYNC_EVENT, handleProgressChanged);
    return () => window.removeEventListener(ACCOUNT_SYNC_EVENT, handleProgressChanged);
  }, [scheduleAutosave]);

  useEffect(() => {
    function flushOnHide() {
      if (document.visibilityState === "hidden") {
        // Immediate, but NOT force: the flush-on-hide must respect the
        // empty-state and remote-newer guards, or a fresh tab closing could
        // still clobber a real backup.
        scheduleAutosave({ immediate: true });
      }
    }

    document.addEventListener("visibilitychange", flushOnHide);
    return () => document.removeEventListener("visibilitychange", flushOnHide);
  }, [scheduleAutosave]);

  return {
    ...status,
    saveNow: () => autosave({ force: true }),
  };
}
